<template>
  <component :is="element" :class="rootClass" v-bind="linkBind">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  TypographyColor,
  type TypographyElement,
  type TypographyKey,
  getTypographyClass,
} from './types'

export type TypographyCustomClass =
  | string
  | string[]
  | Record<string, boolean>
  | undefined

/**
 * @example Inline link (opens in new tab). Import `TypographyKey` from `./types`.
 * ```vue
 * <Typography
 *   :type="TypographyKey.BodyMReg"
 *   is-link
 *   href="https://example.com"
 *   target="_blank"
 * >
 *   Learn more
 * </Typography>
 * ```
 */
export interface TypographyProps {
  type: TypographyKey
  as?: TypographyElement // Ignored when `isLink` is true (root is always `<a>`)
  isLink?: boolean
  href?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  color?: TypographyColor
  customClass?: TypographyCustomClass
}

const props = withDefaults(defineProps<TypographyProps>(), {
  as: 'div',
  isLink: false,
  color: TypographyColor.Primary900
})

const element = computed(() => (props.isLink ? 'a' : props.as))
const linkBind = computed(() => {
  if (!props.isLink) return {}
  const href = props.href?.trim()
  const bind: Record<string, string> = { href: href || '#' }
  if (props.target) {
    bind.target = props.target
    if (props.target === '_blank') {
      bind.rel = 'noopener noreferrer'
    }
  }
  return bind
})
const typographyClass = computed(() =>
  getTypographyClass(props.type, props.isLink)
)
const rootClass = computed(() =>
  [
    typographyClass.value,
    `text-${props.color}`,
    props.customClass,
  ].filter(Boolean)
)
</script>

<style lang="scss" scoped>
// 16px
.fst-body-xl-semi-bold {
  @include ftSterlingText(1rem, 600, 0.2px, 1.5rem);
}

// 16px
.fst-body-xl-reg {
  @include ftSterlingText(1rem, 400, 0.2px, 1.5rem);
}

// 14px
.fst-body-l-semi-bold {
  @include ftSterlingText(0.875rem, 600, 0.2px, 1.25rem);
}

// 14px
.fst-body-l-reg {
  @include ftSterlingText(0.875rem, 400, 0.2px, 1.25rem);
}

// 12px
.fst-body-m-semi-bold {
  @include ftSterlingText(0.75rem, 600, 0.2px, 1rem);
}

// 12px
.fst-body-m-reg {
  @include ftSterlingText(0.75rem, 400, 0.2px, 1rem);
}

// 12px
.fst-body-m-light {
  @include ftSterlingText(0.75rem, 300, 0.2px, 1rem);
}

// 10px
.fst-body-xs-semi-bold {
  @include ftSterlingText(0.625rem, 600, 0.2px, 1rem);
}

// 10px
.fst-body-xs-reg {
  @include ftSterlingText(0.625rem, 400, 0.2px, 1rem);
}

// 10px
.fst-body-xs-light {
  @include ftSterlingText(0.625rem, 300, 0.2px, 1rem);
}

// 32px
.fst-header-xl-semi-bold {
  @include ftSterlingText(2rem, 600, 0.2px, 2.75rem);
}

// 24px
.fst-header-l-semi-bold {
  @include ftSterlingText(1.5rem, 600, 0.2px, 2rem);
}

// 21px
.fst-header-m-semi-bold {
  @include ftSterlingText(1.313rem, 600, 0.2px, 1.75rem);
}

// 18px
.fst-header-s-semi-bold {
  @include ftSterlingText(1.125rem, 600, 0.2px, 1.5rem);
}

// 18px
.fst-header-s-reg {
  @include ftSterlingText(1.125rem, 400, 0.2px, 1.5rem);
}

.text-primary-500 {
  color: $rebranding-primary-500;
}

.text-primary-700 {
  color: $rebranding-primary-700;
}

.text-grey-600 {
  color: $rebranding-grey-600;
}

.text-grey-700 {
  color: $rebranding-grey-700;
}

.text-error-500 {
  color: $rebranding-error-500;
}

.text-success-500 {
  color: $rebranding-success-500;
}

.text-warning-500 {
  color: $rebranding-warning-500;
}

.text-secondary-500 {
  color: $rebranding-secondary-500;
}

.text-secondary-50 {
  color: $rebranding-primary-50;
}

.fst-link-variant {
  color: $rebranding-primary-500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $rebranding-primary-700;
  }
}
</style>
